/**
 * Local "have I read this conversation?" tracker.
 *
 * Background: the Worker emits `unread: true` on a ConversationSummary based
 * on the GHL `unreadCount` field, which we DON'T currently zero out
 * server-side when the owner opens a conversation in the PWA. So opening a
 * thread used to leave the blue unread dot stuck on the list row even after
 * you'd visibly read the message. (Ernie called this out 2026-05-03.)
 *
 * This module persists per-conversation "last read at" timestamps in
 * localStorage. The Conversations list overlays this on top of the Worker's
 * `unread` flag — a row is considered read locally when the most recent
 * message timestamp is at-or-before the locally stored read timestamp. New
 * messages arriving after that re-trigger unread, which is the correct
 * behavior.
 *
 * No server round-trip required. Survives PWA reinstalls (localStorage
 * persists across iOS PWA delete+re-add). Bounded growth: caps at 500
 * tracked convos, oldest-pruned, which is plenty for an SS client.
 */

const STORAGE_KEY = 'ss_convo_read_state_v1';
const MAX_TRACKED = 500;

interface ReadStateMap {
  // conversation_id -> ISO timestamp of the latest message the user saw
  [convoId: string]: string;
}

function read(): ReadStateMap {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as ReadStateMap;
    }
    return {};
  } catch {
    return {};
  }
}

function write(map: ReadStateMap): void {
  try {
    // Cap at MAX_TRACKED entries by dropping oldest (lexicographic ISO
    // sort ascending → oldest first → slice off).
    const entries = Object.entries(map);
    let trimmed: ReadStateMap = map;
    if (entries.length > MAX_TRACKED) {
      entries.sort((a, b) => (a[1] < b[1] ? -1 : 1));
      const kept = entries.slice(entries.length - MAX_TRACKED);
      trimmed = Object.fromEntries(kept);
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    /* ignore — private mode etc. */
  }
}

/**
 * Mark a conversation as read up to the given timestamp. Call this when
 * the user opens the conversation detail.
 */
export function markConversationRead(convoId: string, lastMessageIso: string): void {
  if (!convoId) return;
  const map = read();
  // Only advance — never regress. If we already recorded a later read
  // timestamp for this convo, leave it alone.
  const existing = map[convoId];
  if (existing && existing >= lastMessageIso) return;
  map[convoId] = lastMessageIso || new Date().toISOString();
  write(map);
}

/**
 * Is this conversation locally read at-or-after the given last-message
 * timestamp? When true, the UI should suppress the unread indicator
 * even if the Worker's `unread` flag is true.
 */
export function isConversationLocallyRead(
  convoId: string,
  lastMessageIso: string,
): boolean {
  if (!convoId || !lastMessageIso) return false;
  const map = read();
  const last = map[convoId];
  if (!last) return false;
  return last >= lastMessageIso;
}

// ===========================================================================
// Force-unread state — for swipe-action "Mark unread"
// ===========================================================================
//
// The user can explicitly mark a conversation unread via swipe. We store the
// id in a separate Set so it overrides the Worker's unread flag (which may
// say `unread: false`) AND any prior locally-read state. Tapping the convo
// auto-clears the force-unread (because it'll then run markConversationRead).

const FORCE_UNREAD_KEY = 'ss_convo_force_unread_v1';

function readForceUnread(): Set<string> {
  try {
    const raw = window.localStorage.getItem(FORCE_UNREAD_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) return new Set(parsed.filter((x): x is string => typeof x === 'string'));
    return new Set();
  } catch {
    return new Set();
  }
}

function writeForceUnread(set: Set<string>): void {
  try {
    window.localStorage.setItem(FORCE_UNREAD_KEY, JSON.stringify(Array.from(set)));
  } catch {
    /* ignore */
  }
}

/** Mark a conversation as unread (force the blue dot to show). */
export function markConversationUnread(convoId: string): void {
  if (!convoId) return;
  const set = readForceUnread();
  set.add(convoId);
  writeForceUnread(set);
  // Also remove from the read map so isConversationLocallyRead returns false.
  const map = read();
  if (convoId in map) {
    delete map[convoId];
    write(map);
  }
}

/** Is this conversation explicitly marked unread by the user? */
export function isConversationForceUnread(convoId: string): boolean {
  if (!convoId) return false;
  return readForceUnread().has(convoId);
}

/** Clear the force-unread flag (called automatically when marking read). */
function clearForceUnread(convoId: string): void {
  const set = readForceUnread();
  if (set.delete(convoId)) writeForceUnread(set);
}

// Re-export markConversationRead with auto-clearing of force-unread so the
// two states never disagree.
const _origMarkRead = markConversationRead;
export function markConversationReadAndClearUnread(
  convoId: string,
  lastMessageIso: string,
): void {
  _origMarkRead(convoId, lastMessageIso);
  clearForceUnread(convoId);
}

// ===========================================================================
// Pin state — for swipe-action "Pin to top"
// ===========================================================================

const PINNED_KEY = 'ss_convo_pinned_v1';

function readPinned(): Set<string> {
  try {
    const raw = window.localStorage.getItem(PINNED_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) return new Set(parsed.filter((x): x is string => typeof x === 'string'));
    return new Set();
  } catch {
    return new Set();
  }
}

function writePinned(set: Set<string>): void {
  try {
    window.localStorage.setItem(PINNED_KEY, JSON.stringify(Array.from(set)));
  } catch {
    /* ignore */
  }
}

/** Toggle a conversation's pinned state. Returns the new state. */
export function toggleConversationPinned(convoId: string): boolean {
  if (!convoId) return false;
  const set = readPinned();
  const wasPinned = set.has(convoId);
  if (wasPinned) set.delete(convoId);
  else set.add(convoId);
  writePinned(set);
  return !wasPinned;
}

/** Is this conversation pinned? */
export function isConversationPinned(convoId: string): boolean {
  if (!convoId) return false;
  return readPinned().has(convoId);
}
